import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";

export interface RepositoryInfo {
  id: string;
  org_id: string;
  name: string;
  slug: string;
  git_remote_url: string;
  is_active: boolean;
  created_at: string;
}

export interface RepositoryConfig {
  repository_id: string;
  settings: Record<string, unknown>;
}

export interface RepositoryMembership {
  id: string;
  repository_id: string;
  member_id: string;
  role_override: string | null;
  autonomy_override: string | null;
  created_at: string;
}

interface RepositoryDetailData {
  repository: RepositoryInfo;
  config: RepositoryConfig;
  memberships: RepositoryMembership[];
}

export function useRepositoryDetail(repoId: string | null) {
  const query = useQuery<RepositoryDetailData>({
    queryKey: queryKeys.repositories.detail(repoId ?? ""),
    queryFn: async () => {
      const [repository, config, memberships] = await Promise.all([
        api<RepositoryInfo>(`/api/v2/repositories/${repoId}`),
        api<RepositoryConfig>(`/api/v2/repositories/${repoId}/config`),
        api<RepositoryMembership[]>(
          `/api/v2/repositories/${repoId}/memberships`,
        ),
      ]);
      return { repository, config, memberships };
    },
    enabled: repoId !== null,
  });

  const refetch = useCallback(() => {
    void query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  return {
    repository: query.data?.repository ?? null,
    config: query.data?.config ?? null,
    memberships: query.data?.memberships ?? [],
    loading: query.isLoading,
    error: query.error ? "Failed to load repository details" : "",
    refetch,
  };
}
